import React, {
	ChangeEvent,
	FormEvent,
	useEffect,
	useReducer,
	useState,
} from 'react';
import {
	getSealDetailsUrl,
	getSealByPartNumber,
	sealSearchContent,
	getSealDetailsDrawUrl,
	getCategoryFromResponse,
	transformSealData
} from '../../../components/bearing-search/api/api';
import { sealSpecsColumnLabels, text, unitDimensionList } from '../../data';
import TextInput from '../../../components/bearing-search/components/forms/TextInput';
import SelectDropdown from '../../../components/bearing-search/components/forms/SelectDropdown';
import {
	SealSortPayload,
	TimkenUnitDimension,
} from '../../../components/bearing-search/types/types';
import SealSearchResults from '../../components/seal-search-results/SealSearchResults';
import sealPartReducer, { initialSealPartState } from './reducer';
import { trackProductResults, updateActionParams } from '../../utils/analytics';
import { toggleNextTools, getPartNumberFromUrl } from '../../utils/engineering-tools';
import PartPreSearch from '../../components/PartPreSearch';

const PartNumberSearch: React.FC = () => {
	const [ isPartSelected, setIsPartSelected ] = useState<boolean>(null);
	const [ verifiedPart, setVerifiedPart ] = useState<string | undefined>( undefined );
	const formDescription = sealSearchContent.partNumberDesc
		? { __html: sealSearchContent.partNumberDesc }
		: undefined;
	const [ isSearched, setIsSearched ] = useState( false );
	const [ results, setResults ] = useState<string[][]>( [] );
	const [ columnKeys, setColumnKeys ] = useState<string[]>( [] );
	const [ isResultsLoading, setIsResultsLoading ] = useState( false );
	const [ isRefinementOnly, setIsRefinementOnly ] = useState( false );
	const [ formState, dispatch ] = useReducer(
		sealPartReducer,
		initialSealPartState,
	);

	useEffect( () => {
		const isShown = isSearched && ! isResultsLoading;
		toggleNextTools( isShown );

		return () => toggleNextTools( false );
	}, [ isSearched, isResultsLoading ] );

	const handleFormChange = () => {
		dispatch( {
			type: 'change_validation_flag',
			payload: false,
		} );

		dispatch( {
			type: 'update_request_data',
		} );
	};

	const handleSubmit = ( event: FormEvent ) => {
		event.preventDefault();

		dispatch( {
			type: 'change_validation_flag',
			payload: true,
		} );

		if ( formState.requestData ) {
			setIsSearched( true );
			setIsResultsLoading( true );

			const { partNumber, unitDimension } = formState;

			setIsRefinementOnly( false );

			getSealByPartNumber( { partNumber } )
				.then( ( response ) => {
					const category = getCategoryFromResponse( response );

					document.querySelector('.t-engineering-tools__results').scrollIntoView({
						behavior: 'smooth',
					});

					if ( ! category ) {
						setResults( [] );
						return;
					}

					const sealResults = transformSealData( response, unitDimension );
					trackProductResults( 'sealSearch', { partNumber }, sealResults.length, partNumber );
					updateActionParams( 'sealSearch' );
					setColumnKeys( category.responseFields[ formState.unitDimension ] );
					setResults( sealResults );
				} )
				.finally( () => setIsResultsLoading( false ) );
		}
	};

	const handleSort = ( sortOptions?: SealSortPayload ) => {
		if ( ! formState.requestData ) {
			return;
		}

		const { requestData, unitDimension } = formState;

		const request = {
			...requestData,
			...sortOptions,
		};

		setIsRefinementOnly( true );

		getSealByPartNumber( request )
			.then( ( response ) => {
				const category = getCategoryFromResponse( response );
				if ( ! category ) {
					setResults( [] );
					return;
				}

				updateActionParams( 'sealSearch' );
				setColumnKeys( category.responseFields[ formState.unitDimension ] );
				setResults( transformSealData( response, unitDimension ) );
			} )
			.finally( () => setIsResultsLoading( false ) );
	};

	const clearSearch = () => {
		setIsSearched( false );
		setIsPartSelected( false );

		dispatch( {
			type: 'reset',
		} );
	};
	useEffect(() => {
		const partNumberFromUrl = getPartNumberFromUrl();
		if (partNumberFromUrl) {
			dispatch({
				type: 'change_part_number',
				payload: partNumberFromUrl,
			});
			setIsPartSelected( true );
		}
	}, []);

	const preSearchClickHandler = () => {
		setIsPartSelected(true);
	};

	const setVerifiedPartAndPartNumber = (value) => {
		setVerifiedPart( value );

		dispatch( {
			type: 'change_part_number',
			payload: value as string,
		} )
	};

	const inputPartNumber = ( event: ChangeEvent<HTMLInputElement> ) => {
		const value = event.target.value as string;
		dispatch( {
			type: 'change_part_number',
			payload: value,
		} );
		setIsPartSelected( false );
	};

	return (
		<div>
			<form className="part-number-search-form" onSubmit={handleSubmit} onChange={handleFormChange}>
				{!! formDescription && (
					<p
						className="t-engineering-tools__description"
						dangerouslySetInnerHTML={formDescription}
					></p>
				)}

				<p>
					<span className="c-form__asterisk">*</span>
					{text.requiredMessage}
				</p>

				<div className="t-engineering-tools__form-fields timken-tool-presearch-container">
					<TextInput
						id="seal_part_part_number"
						required={true}
						label={text.sealSearchPartNumber}
						name="partNumber"
						value={formState.partNumber}
						placeholder={text.sealSearchPartNumber}
						invalid={formState.isValidated && ! formState.isPartNumberValid}
						message={text.sealWildCardHint}
						errorMessage={text.sealValidationHint}
						onChange={inputPartNumber}
					/>
					<PartPreSearch
						input={formState.partNumber}
						isHidden={formState.partNumber.length < 2 || (isSearched && isResultsLoading) || isPartSelected}
						unit={formState.unitDimension}
						onClick={preSearchClickHandler}
						sealSearchType={'PartNumber'}
						verifyPartNumber={(value: string) => setVerifiedPartAndPartNumber(value)}
					/>

					<SelectDropdown
						id="seal_part_unit"
						required={true}
						label={text.displayUnits}
						value={formState.unitDimension}
						options={unitDimensionList}
						onChange={( event: ChangeEvent<HTMLSelectElement> ) =>
							dispatch( {
								type: 'change_unit',
								payload: event.target.value as TimkenUnitDimension,
							} )
						}
					/>
				</div>

				<div className="t-engineering-tools__search-actions">
					<button
						className="btn btn--primary"
						type="submit"
						disabled={formState.isValidated}
					>
						{text.search}
					</button>

					<button
						className="btn btn--secondary"
						type="button"
						onClick={() => clearSearch()}
					>
						{text.clear}
					</button>
				</div>
			</form>

			{isSearched && (
				<SealSearchResults
					data={results}
					refining={isRefinementOnly}
					columnKeys={columnKeys}
					columnLabels={sealSpecsColumnLabels}
					loading={isResultsLoading}
					urlComposer={getSealDetailsUrl}
					drawUrlComposer={getSealDetailsDrawUrl}
					onSort={handleSort}
					unit={formState.unitDimension}
				/>
			)}
		</div>
	);
};

export default PartNumberSearch;
